var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useState } from 'react';
import { Anchor, TypographyBody, Button, FacebookIcon, GoogleIcon, PasswordField, Stacker, TextField, useHoneycombTheme, Styler, } from '@honeycomb/ui-core';
import { useAccount, useGlobalContext, useSignIn, ExternalProvider, useExternalSignIn, parseBoolean, } from '@honeycomb/data';
import { AccountScreenEnum, accountScreenRouting, router } from '@honeycomb/routing';
import { GoogleLogin } from 'react-google-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { AccountScreenContainer } from '../utils/AccountScreenContainer';
import { ValidatorField } from '../../validator/ValidatorField';
import { validateEmail } from '../../validator/utils';
import { useValidator } from '../../validator/useValidator';
import { ValidatorContext } from '../../validator/ValidatorContext';
import { accountFieldVerify } from '../../../utils/accountFieldVerify';
var SignInPages;
(function (SignInPages) {
    SignInPages["Username"] = "Username";
    SignInPages["Password"] = "Password";
})(SignInPages || (SignInPages = {}));
// It seems that the config strings don't align with what GraphQL is expecting
// This function maps possible config strings to concrete ExternalProvider type (accepted by GraphQL)
const mapGoogleAuthProvider = (val) => {
    const map = {
        GooglePeople: ExternalProvider.Googlepeople,
    };
    return map[val] || ExternalProvider.Googlepeople;
};
const signUpLabel = 'Sign Up';
const forgotPasswordLabel = 'Forgot your password?';
const backToEmailLabel = 'Back';
export function AccountSignIn({ socialMediaLogin = true }) {
    const { resourceStrings: { LOGIN_SIGN_IN_HEADING, LOGIN_SIGN_IN_SUBHEADING, LOGIN_SIGN_IN_BUTTON, LOGIN_SIGN_IN_DIVIDER, LOGIN_SIGN_IN_CREATE_PROMPT, LOGIN_SIGN_IN_PASSWORD_HEADING, LOGIN_SIGN_IN_PASSWORD_SUBHEADING, LOGIN_SIGN_IN_PASSWORD_BUTTON, LOGIN_SIGN_IN_FAILED, LOGIN_SIGN_IN_SOCIAL_FAILED, LOGIN_EMAIL_INVALID, }, configSettings: { GoogleAppId, GoogleAuthProvider, FacebookAppId, HCMybFacebookLoginEnabled, HCMybGoogleLoginEnabled, }, } = useGlobalContext();
    const [formPage, setFormPage] = useState(SignInPages.Username);
    const [googleSignInDisabled, setGoogleSignInDisabled] = useState(false);
    const [socialSignInFailed, setSocialSignInFailed] = useState(null);
    const { refreshSignedIn } = useAccount();
    const { theme } = useHoneycombTheme();
    const { signIn, signInState } = useSignIn([refreshSignedIn, () => accountScreenRouting(null)]);
    const { loading } = signInState;
    const { externalSignIn } = useExternalSignIn([refreshSignedIn, () => accountScreenRouting(null)]);
    const { context: validatorContext, validateAll } = useValidator(['sign-in-email-field']);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [signInError, setSignInError] = React.useState(false);
    const emailFormSubmitted = () => {
        validateAll(['sign-in-email-field'], (count) => {
            if (count === 0) {
                setFormPage(SignInPages.Password);
                setPassword('');
            }
        });
    };
    const passwordFormSubmitted = () => {
        signIn(email, password).catch(() => {
            setSignInError(true);
            setPassword('');
        });
    };
    const getSocialErrorMessage = (provider) => {
        if (provider === mapGoogleAuthProvider(GoogleAuthProvider)) {
            return (LOGIN_SIGN_IN_SOCIAL_FAILED && LOGIN_SIGN_IN_SOCIAL_FAILED.replace('{0}', 'Google')) || '';
        }
        return (LOGIN_SIGN_IN_SOCIAL_FAILED && LOGIN_SIGN_IN_SOCIAL_FAILED.replace('{0}', 'Facebook')) || '';
    };
    const handleSocialFailure = (e, provider) => {
        // eslint-disable-next-line no-console
        console.error('Social login failure', e);
        if (e.error === 'idpiframe_initialization_failed') {
            setGoogleSignInDisabled(true);
        }
        else {
            setSocialSignInFailed(provider);
        }
    };
    const handleSocial = (token, provider) => __awaiter(this, void 0, void 0, function* () {
        const accessToken = token === null || token === void 0 ? void 0 : token.accessToken;
        setSocialSignInFailed(null);
        if (accessToken && provider) {
            externalSignIn(accessToken, provider).catch(() => {
                handleSocialFailure(token, provider);
            });
        }
        else {
            handleSocialFailure(token, provider);
        }
    });
    const onAccountScreenRoutingSubmit = useCallback((e) => __awaiter(this, void 0, void 0, function* () {
        e.preventDefault();
        try {
            yield accountScreenRouting(AccountScreenEnum.FORGOT_PASSWORD);
        }
        catch (error) {
            console.log('accountScreenRouting err', error);
        }
    }), []);
    const usernameFooter = (React.createElement(Stacker, { spacing: 4, pt: 6, alignItems: "center" },
        socialMediaLogin && React.createElement("div", null, LOGIN_SIGN_IN_DIVIDER),
        GoogleAppId && socialMediaLogin && parseBoolean(HCMybGoogleLoginEnabled) && (React.createElement(GoogleLogin, { style: { width: '100%' }, clientId: GoogleAppId, scope: "openid profile email", responseType: "token", onSuccess: (e) => handleSocial(e, mapGoogleAuthProvider(GoogleAuthProvider)), onFailure: (e) => handleSocialFailure(e, mapGoogleAuthProvider(GoogleAuthProvider)), render: (renderProps) => (React.createElement(Button, { fullWidth: true, color: "primary", variant: "outlined", icon: React.createElement(GoogleIcon, null), onClick: renderProps.onClick, disabled: googleSignInDisabled }, "Sign in with Google")) })),
        FacebookAppId && socialMediaLogin && parseBoolean(HCMybFacebookLoginEnabled) && (React.createElement(FacebookLogin, { appId: FacebookAppId, scope: "email", authType: "rerequest", callback: (e) => handleSocial(e, ExternalProvider.Facebook), responseType: "token", redirectUri: router.currentRoute(), render: (renderProps) => (React.createElement(Button, { fullWidth: true, color: "primary", variant: "outlined", icon: React.createElement(FacebookIcon, { style: { fill: theme.palette.social.facebook.blue } }), c: theme.palette.social.facebook.blue, onClick: renderProps.onClick }, "Sign in with Facebook")) })),
        socialSignInFailed && socialMediaLogin && (React.createElement(Styler, { p: 2 },
            React.createElement(TypographyBody, { color: "system.error.base" }, getSocialErrorMessage(socialSignInFailed)))),
        React.createElement("div", null,
            `${LOGIN_SIGN_IN_CREATE_PROMPT} ` || ' ',
            React.createElement(Anchor, { underline: "always", component: "button", color: "text.link", onClick: () => accountScreenRouting(AccountScreenEnum.SIGN_UP) }, signUpLabel)),
        React.createElement(Anchor, { underline: "always", component: "button", color: "text.link", onClick: () => accountScreenRouting(null) }, "Back")));
    const passwordFooter = (React.createElement(React.Fragment, null,
        signInError && (React.createElement(TypographyBody, { color: "system.error.base", mt: 2 }, LOGIN_SIGN_IN_FAILED || '')),
        React.createElement(Styler, { mt: 4 },
            React.createElement(Styler, { ta: "center" },
                React.createElement(Anchor, { underline: "always", component: "button", color: "text.link", onClick: onAccountScreenRoutingSubmit }, forgotPasswordLabel))),
        React.createElement(Styler, { mt: 4 },
            React.createElement(Styler, { ta: "center" },
                React.createElement(Anchor, { underline: "always", component: "button", color: "text.link", onClick: (e) => {
                        e.preventDefault();
                        setFormPage(SignInPages.Username);
                        setPassword('');
                        setSignInError(false);
                    } }, backToEmailLabel)))));
    const page = () => {
        switch (formPage) {
            case SignInPages.Username:
                return (React.createElement(AccountScreenContainer, { onFormSubmit: emailFormSubmitted, formId: "sign-in-username-form", heading: LOGIN_SIGN_IN_HEADING || '', subHeading: LOGIN_SIGN_IN_SUBHEADING || '', buttonLabel: LOGIN_SIGN_IN_BUTTON || '', footer: usernameFooter },
                    React.createElement(ValidatorField, { id: "sign-in-email-field", validate: accountFieldVerify(validateEmail), invalidText: () => LOGIN_EMAIL_INVALID || '', validateOnBlur: true, validateOnChange: true },
                        React.createElement(TextField, { size: "l", id: "sign-in-email", name: "email", label: "Email", autoFocus: true, autoComplete: "email username", fullWidth: true, type: "email", value: email, onChange: (e) => setEmail(e.target.value.trim()), onFocus: () => setSocialSignInFailed(null) }))));
            case SignInPages.Password:
                return (React.createElement(AccountScreenContainer, { onFormSubmit: passwordFormSubmitted, formId: "sign-in-password-form", heading: LOGIN_SIGN_IN_PASSWORD_HEADING || '', subHeading: LOGIN_SIGN_IN_PASSWORD_SUBHEADING || '', buttonLabel: LOGIN_SIGN_IN_PASSWORD_BUTTON || '', footer: passwordFooter, loading: loading, hidden: 
                    // This input is so that password managers can scrape the correct email from the form.
                    React.createElement("input", { style: { display: 'none' }, type: "text", value: email, autoComplete: "email username", readOnly: true }) },
                    React.createElement(PasswordField, { id: "sign-in-password", name: "password", label: "Password", autoFocus: true, autoComplete: "current-password", fullWidth: true, value: password, onChange: (e) => setPassword(e.target.value), onFocus: () => setSignInError(false), size: "l" })));
            default:
                return null;
        }
    };
    return React.createElement(ValidatorContext.Provider, { value: validatorContext }, page());
}
